
'use client';
import { useEffect } from 'react';
import { getMessaging, onMessage } from 'firebase/messaging';
import { useAuth } from '@/context/auth-context';
import { useNotifications } from '@/context/notifications-context';
import { getFCMTokenAndSave } from '@/firebase/fcm';
import { firebaseApp } from '@/firebase/client';

export function FCMInitializer() {
  const { authUser, user } = useAuth();
  const { addNotification } = useNotifications();
  // Gate every FCM activity on a verified, signed-in user. There's no value
  // in registering the service worker, getting a token, or wiring an
  // onMessage listener for an anonymous browser — pushes are only ever
  // addressed to authenticated users, and keeping the SW unregistered
  // until sign-in avoids the post-logout case where the SW outlives auth
  // and can still receive pushes for the previous account.
  const ready = !!(authUser && user && authUser.emailVerified);

  useEffect(() => {
    if (!ready) return;
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
    if (!('Notification' in window) || Notification.permission === 'denied') return;

    let cancelled = false;
    let registration: ServiceWorkerRegistration | null = null;
    navigator.serviceWorker.register('/firebase-messaging-sw.js')
      .then(reg => {
        if (cancelled) {
          // Effect already torn down (logout while register was in flight) —
          // unregister immediately so we don't strand a SW we no longer want.
          reg.unregister().catch(() => {});
          return;
        }
        registration = reg;
      }).catch(() => {
        // SW registration failures are non-fatal — pushes just won't work for
        // this session. No user-facing action to take.
      });

    return () => {
      cancelled = true;
      // Tear down on logout / unmount so a subsequent push isn't proxied to
      // the foreground listener for the previous user.
      if (registration) registration.unregister().catch(() => {});
    };
  }, [ready]);

  useEffect(() => {
    if (!ready) return;
    // Firestore rule on users/{uid}/fcmTokens requires isVerifiedOwner; we
    // only get here when that's already true via `ready`.
    getFCMTokenAndSave(authUser!.uid);
  }, [ready, authUser]);

  useEffect(() => {
    if (!ready) return;
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;

    const messaging = getMessaging(firebaseApp);
    const unsubscribe = onMessage(messaging, (payload) => {
      if (payload.notification) {
        addNotification({
          type: 'toast',
          title: payload.notification.title || 'New Message',
          description: payload.notification.body,
        });
      }
    });
    return () => unsubscribe();
  }, [ready, addNotification]);

  return null; // This component doesn't render anything
}
